import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Sparkles, Brain, TrendingUp, TrendingDown, AlertTriangle, 
  CheckCircle2, Lightbulb, Target, DollarSign, Users, 
  BarChart3, PieChart, Activity, RefreshCw, Download,
  MessageSquare, ChevronRight, Loader2, FileText, Zap,
  ArrowUpRight, ArrowDownRight, Clock, Building2, Rocket
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { LaunchCampaignButton } from './LaunchCampaignButton';

import { API_BASE_URL as API } from '../utils/apiConfig';

// Format helpers
const formatNumber = (num) => {
  if (num === null || num === undefined) return '—';
  if (Math.abs(num) >= 10000000) return `${(num / 10000000).toFixed(2)} Cr`;
  if (Math.abs(num) >= 100000) return `${(num / 100000).toFixed(2)} L`;
  if (Math.abs(num) >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString('en-IN');
};

const formatCurrency = (num) => `₹${formatNumber(num)}`;

// Insight Card Component with Campaign Activation
const InsightCard = ({ insight, onAskAI, priority = 'medium', brandId, onCampaignCreated }) => {
  const priorityColors = {
    high: 'border-red-500/50 bg-red-500/5',
    medium: 'border-amber-500/50 bg-amber-500/5',
    low: 'border-green-500/50 bg-green-500/5'
  };
  
  const priorityBadges = {
    high: 'bg-red-500 text-white',
    medium: 'bg-amber-500 text-white',
    low: 'bg-green-500 text-white'
  };
  
  const priorityMap = {
    high: 'P0',
    medium: 'P1',
    low: 'P2'
  };
  
  const severityIcons = {
    success: <CheckCircle2 className="w-5 h-5 text-green-500" />,
    warning: <AlertTriangle className="w-5 h-5 text-amber-500" />,
    danger: <AlertTriangle className="w-5 h-5 text-red-500" />,
    info: <Lightbulb className="w-5 h-5 text-blue-500" />
  };
  
  return (
    <Card className={`${priorityColors[priority]} transition-all hover:shadow-lg`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-1">
            {severityIcons[insight.severity] || severityIcons.info}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="font-semibold text-sm">{insight.title}</h4>
              <Badge className={`text-xs ${priorityBadges[priority]}`}>
                {priority.toUpperCase()}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mb-3">{insight.insight}</p>
            
            {insight.data_points && (
              <div className="flex flex-wrap gap-2 mb-3">
                {Object.entries(insight.data_points).slice(0, 3).map(([key, value]) => (
                  <Badge key={key} variant="outline" className="text-xs">
                    {key.replace(/_/g, ' ')}: {value}
                  </Badge>
                ))}
              </div>
            )}
            
            {insight.recommendation && (
              <div className="p-2 rounded bg-muted/50 mb-3">
                <p className="text-xs font-medium text-primary flex items-center gap-1">
                  <Target className="w-3 h-3" />
                  {insight.recommendation}
                </p>
              </div>
            )}
            
            <div className="flex items-center justify-between gap-2">
              {insight.impact && (
                <span className="text-xs text-muted-foreground">
                  Impact: {insight.impact}
                </span>
              )}
              <div className="flex items-center gap-2">
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="gap-1 text-xs h-7"
                  onClick={() => onAskAI?.(insight)}
                >
                  <MessageSquare className="w-3 h-3" />
                  Ask AI
                </Button>
                {brandId && insight.recommendation && (
                  <LaunchCampaignButton
                    segmentName={insight.title}
                    customers={insight.customers || 0}
                    brandId={brandId}
                    insight={insight}
                    recommendation={{
                      action: insight.recommendation,
                      priority: priorityMap[priority],
                      impact: insight.impact
                    }}
                    onCampaignCreated={onCampaignCreated}
                    size="sm"
                    className="h-7 text-xs bg-violet-500 hover:bg-violet-600"
                    buttonText="Activate"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Recommendation Card Component with Campaign Launch
const RecommendationCard = ({ recommendation, onActivate, onAskAI, brandId, onCampaignCreated }) => {
  return (
    <Card className="border-violet-500/30 bg-gradient-to-br from-violet-500/5 to-purple-500/5">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Badge className={`${recommendation.priority === 'P0' ? 'bg-red-500' : recommendation.priority === 'P1' ? 'bg-amber-500' : 'bg-blue-500'} text-white`}>
              {recommendation.priority}
            </Badge>
            <span className="text-sm font-medium">{recommendation.action}</span>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4 mb-3 text-xs">
          <div>
            <span className="text-muted-foreground">Owner:</span>
            <span className="ml-1 font-medium">{recommendation.owner}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Impact:</span>
            <span className="ml-1 font-medium text-green-500">{recommendation.expected_impact || recommendation.impact}</span>
          </div>
        </div>
        
        <div className="flex gap-2">
          <LaunchCampaignButton
            segmentName={recommendation.segment || recommendation.action?.substring(0, 30)}
            customers={recommendation.target_customers || 0}
            brandId={brandId}
            recommendation={recommendation}
            onCampaignCreated={onCampaignCreated}
            size="sm"
            className="flex-1 gap-1 bg-violet-500 hover:bg-violet-600"
            buttonText="Activate Campaign"
          />
          <Button size="sm" variant="outline" className="gap-1" onClick={() => onAskAI?.(recommendation)}>
            <MessageSquare className="w-3 h-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Main Brand AI Insights Component
export default function BrandAIInsights({ brandId, brandName, onNavigateToChat }) {
  const [loading, setLoading] = useState(true);
  const [insightsData, setInsightsData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [generatingPDF, setGeneratingPDF] = useState(false);
  
  const fetchInsights = useCallback(async () => {
    if (!brandId) return;
    
    setLoading(true);
    try {
      // Fetch insights from all dashboards
      const [commandCenter, rfmData, cohortData, storeData, campaignData] = await Promise.all([
        axios.post(`${API}/api/analytics/command-center`, { brand_id: brandId }, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.post(`${API}/api/analytics/rfm-churn`, { brand_id: brandId }, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.post(`${API}/api/analytics/cohort-migration`, { brand_id: brandId }, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.post(`${API}/api/analytics/store-performance`, { brand_id: brandId }, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/api/campaigns/analytics-summary?brand_id=${brandId}`, { withCredentials: true }).catch(() => ({ data: {} }))
      ]);
      
      // Aggregate all insights - ensure arrays are properly handled
      const commandCenterInsights = commandCenter.data?.data?.insights;
      const rfmInsights = rfmData.data?.data?.insights;
      const cohortInsights = cohortData.data?.data?.insights;
      const storeInsights = storeData.data?.data?.insights;
      
      const allInsights = [
        ...(Array.isArray(commandCenterInsights) ? commandCenterInsights : []),
        ...(Array.isArray(rfmInsights) ? rfmInsights : []),
        ...(Array.isArray(cohortInsights) ? cohortInsights : []),
        ...(Array.isArray(storeInsights) ? storeInsights : [])
      ];
      
      // Categorize insights by priority
      const categorizedInsights = {
        critical: allInsights.filter(i => i.priority === 'high' || i.severity === 'danger'),
        actionable: allInsights.filter(i => i.priority === 'medium' || i.severity === 'warning'),
        opportunities: allInsights.filter(i => i.priority === 'low' || i.severity === 'success' || i.severity === 'info')
      };
      
      // Extract all recommendations
      const allRecommendations = allInsights
        .filter(i => i.recommendation)
        .map(i => ({
          priority: i.priority === 'high' ? 'P0' : i.priority === 'medium' ? 'P1' : 'P2',
          action: i.recommendation,
          owner: 'Analytics Team',
          expected_impact: i.impact || 'Potential uplift',
          source: i.title
        }));
      
      setInsightsData({
        summary: commandCenter.data?.data?.kpis || {},
        insights: categorizedInsights,
        recommendations: allRecommendations.slice(0, 10),
        dashboardData: {
          commandCenter: commandCenter.data?.data,
          rfm: rfmData.data?.data,
          cohort: cohortData.data?.data,
          store: storeData.data?.data,
          campaigns: campaignData.data
        },
        totalInsights: allInsights.length,
        lastUpdated: new Date().toISOString()
      });
      
    } catch (err) {
      console.error('Fetch brand insights error:', err);
      toast.error('Failed to load brand insights');
    } finally {
      setLoading(false);
    }
  }, [brandId]);
  
  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);
  
  const handleAskAI = (item) => {
    // Navigate to chat with pre-filled context
    const context = item.insight || item.action || item.recommendation || item.title;
    if (onNavigateToChat) {
      onNavigateToChat(`Explain this insight in detail: "${context}". What does it mean for our business and what specific actions should we take?`);
    } else {
      toast.info('Opening AI Chat with context...');
    }
  };
  
  const handleGeneratePDF = async () => {
    setGeneratingPDF(true);
    try {
      const response = await axios.post(
        `${API}/api/reports/brand-insights-pdf`,
        { brand_id: brandId, insights: insightsData },
        { withCredentials: true, responseType: 'blob' }
      );
      
      // Download PDF
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${brandName || 'Brand'}_AI_Insights_Report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF report downloaded!');
    } catch (err) {
      console.error('Generate PDF error:', err);
      toast.error('Failed to generate PDF report');
    } finally {
      setGeneratingPDF(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-violet-500" />
          <p className="text-muted-foreground">Analyzing brand data...</p>
        </div>
      </div>
    );
  }
  
  const summary = insightsData?.summary || {};
  const insights = insightsData?.insights || {};
  const recommendations = insightsData?.recommendations || [];
  
  return (
    <div className="space-y-6" data-testid="brand-ai-insights">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600">
              <Brain className="w-6 h-6 text-white" />
            </div>
            Brand AI Insights
          </h1>
          <p className="text-muted-foreground mt-1">
            Comprehensive AI analysis for {brandName || 'your brand'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchInsights}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            size="sm" 
            className="gap-2 bg-violet-500 hover:bg-violet-600"
            onClick={handleGeneratePDF}
            disabled={generatingPDF}
          >
            {generatingPDF ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileText className="w-4 h-4" />
            )}
            Generate PDF Report
          </Button>
        </div>
      </div>
      
      {/* Quick Stats */}
      <div className="grid md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-violet-500/10 to-purple-500/5">
          <CardContent className="p-4 text-center">
            <Sparkles className="w-6 h-6 mx-auto mb-2 text-violet-500" />
            <p className="text-2xl font-bold">{insightsData?.totalInsights || 0}</p>
            <p className="text-xs text-muted-foreground">Total Insights</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-500/10 to-orange-500/5">
          <CardContent className="p-4 text-center">
            <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-red-500" />
            <p className="text-2xl font-bold text-red-500">{insights.critical?.length || 0}</p>
            <p className="text-xs text-muted-foreground">Critical Issues</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500/10 to-yellow-500/5">
          <CardContent className="p-4 text-center">
            <Lightbulb className="w-6 h-6 mx-auto mb-2 text-amber-500" />
            <p className="text-2xl font-bold text-amber-500">{insights.actionable?.length || 0}</p>
            <p className="text-xs text-muted-foreground">Action Items</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/5">
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-6 h-6 mx-auto mb-2 text-green-500" />
            <p className="text-2xl font-bold text-green-500">{insights.opportunities?.length || 0}</p>
            <p className="text-xs text-muted-foreground">Opportunities</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/5">
          <CardContent className="p-4 text-center">
            <Target className="w-6 h-6 mx-auto mb-2 text-blue-500" />
            <p className="text-2xl font-bold text-blue-500">{recommendations.length}</p>
            <p className="text-xs text-muted-foreground">Recommendations</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="critical" className="gap-2">
            <AlertTriangle className="w-4 h-4" />
            Critical ({insights.critical?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="actions" className="gap-2">
            <Lightbulb className="w-4 h-4" />
            Actions ({insights.actionable?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="opportunities" className="gap-2">
            <TrendingUp className="w-4 h-4" />
            Opportunities ({insights.opportunities?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="recommendations" className="gap-2">
            <Target className="w-4 h-4" />
            Recommendations
          </TabsTrigger>
        </TabsList>
        
        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          {/* Executive Summary */}
          <Card className="bg-gradient-to-r from-violet-500/10 via-purple-500/5 to-pink-500/10 border-violet-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-violet-500" />
                AI Executive Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm leading-relaxed">
                Based on comprehensive analysis of {brandName}'s loyalty program data, 
                we've identified <span className="font-semibold text-red-500">{insights.critical?.length || 0} critical issues</span> requiring immediate attention, 
                <span className="font-semibold text-amber-500"> {insights.actionable?.length || 0} actionable items</span> for optimization, 
                and <span className="font-semibold text-green-500">{insights.opportunities?.length || 0} growth opportunities</span>.
                {summary.loyalty_contribution && (
                  <> Your loyalty program contributes <span className="font-bold">{summary.loyalty_contribution.value}%</span> of total revenue, 
                  {summary.loyalty_contribution.value >= 65 ? ' exceeding' : ' below'} the industry benchmark of 65%.</>
                )}
              </p>
            </CardContent>
          </Card>
          
          {/* Key Metrics */}
          {Object.keys(summary).length > 0 && (
            <div className="grid md:grid-cols-4 gap-4">
              {summary.loyalty_contribution && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-muted-foreground">Loyalty Contribution</span>
                      {summary.loyalty_contribution.delta > 0 ? (
                        <ArrowUpRight className="w-4 h-4 text-green-500" />
                      ) : (
                        <ArrowDownRight className="w-4 h-4 text-red-500" />
                      )}
                    </div>
                    <p className="text-2xl font-bold">{summary.loyalty_contribution.value}%</p>
                    <p className="text-xs text-muted-foreground">Target: {summary.loyalty_contribution.benchmark}</p>
                  </CardContent>
                </Card>
              )}
              {summary.active_members && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-muted-foreground">Active Members (90d)</span>
                      <Users className="w-4 h-4 text-blue-500" />
                    </div>
                    <p className="text-2xl font-bold">{formatNumber(summary.active_members.value)}</p>
                    <p className="text-xs text-muted-foreground">{summary.active_members.benchmark}</p>
                  </CardContent>
                </Card>
              )}
              {summary.repeat_ratio && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-muted-foreground">Repeat Ratio</span>
                      <Activity className="w-4 h-4 text-violet-500" />
                    </div>
                    <p className="text-2xl font-bold">{summary.repeat_ratio.value}%</p>
                    <p className="text-xs text-muted-foreground">Target: {summary.repeat_ratio.benchmark}</p>
                  </CardContent>
                </Card>
              )}
              {summary.earn_burn_ratio && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-muted-foreground">Earn-Burn Ratio</span>
                      <DollarSign className="w-4 h-4 text-amber-500" />
                    </div>
                    <p className="text-2xl font-bold">{summary.earn_burn_ratio.value}%</p>
                    <p className="text-xs text-muted-foreground">Target: {summary.earn_burn_ratio.benchmark}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          
          {/* Top Priorities */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Top Priorities</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {[...insights.critical?.slice(0, 2) || [], ...insights.actionable?.slice(0, 2) || []].slice(0, 4).map((insight, idx) => (
                <InsightCard 
                  key={idx} 
                  insight={insight} 
                  priority={insight.priority || (idx < 2 ? 'high' : 'medium')}
                  onAskAI={() => handleAskAI(insight)}
                />
              ))}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Critical Tab */}
        <TabsContent value="critical" className="mt-6">
          <div className="space-y-4">
            {insights.critical?.length > 0 ? (
              insights.critical.map((insight, idx) => (
                <InsightCard 
                  key={idx} 
                  insight={insight} 
                  priority="high"
                  onAskAI={() => handleAskAI(insight)}
                />
              ))
            ) : (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-500" />
                  <h3 className="font-semibold mb-2">No Critical Issues</h3>
                  <p className="text-muted-foreground">Your loyalty program is performing well!</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
        
        {/* Actions Tab */}
        <TabsContent value="actions" className="mt-6">
          <div className="space-y-4">
            {insights.actionable?.length > 0 ? (
              insights.actionable.map((insight, idx) => (
                <InsightCard 
                  key={idx} 
                  insight={insight} 
                  priority="medium"
                  onAskAI={() => handleAskAI(insight)}
                />
              ))
            ) : (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Lightbulb className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="font-semibold mb-2">No Action Items</h3>
                  <p className="text-muted-foreground">Check back later for new recommendations</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
        
        {/* Opportunities Tab */}
        <TabsContent value="opportunities" className="mt-6">
          <div className="space-y-4">
            {insights.opportunities?.length > 0 ? (
              insights.opportunities.map((insight, idx) => (
                <InsightCard 
                  key={idx} 
                  insight={insight} 
                  priority="low"
                  onAskAI={() => handleAskAI(insight)}
                />
              ))
            ) : (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <TrendingUp className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="font-semibold mb-2">Analyzing Opportunities</h3>
                  <p className="text-muted-foreground">AI is looking for growth opportunities...</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
        
        {/* Recommendations Tab */}
        <TabsContent value="recommendations" className="mt-6">
          <div className="grid md:grid-cols-2 gap-4">
            {recommendations.length > 0 ? (
              recommendations.map((rec, idx) => (
                <RecommendationCard 
                  key={idx} 
                  recommendation={rec}
                  onActivate={() => toast.info('Opening Campaign Builder...')}
                  onAskAI={() => handleAskAI(rec)}
                />
              ))
            ) : (
              <Card className="border-dashed col-span-2">
                <CardContent className="py-12 text-center">
                  <Target className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="font-semibold mb-2">No Recommendations Yet</h3>
                  <p className="text-muted-foreground">AI will generate recommendations based on your data</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Last Updated */}
      <div className="text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
        <Clock className="w-3 h-3" />
        Last updated: {new Date(insightsData?.lastUpdated || Date.now()).toLocaleString()}
      </div>
    </div>
  );
}
